/**
 * =============================================================================
 * IDS CRM Web - each person's column order on the Jobs & Tickets board
 * =============================================================================
 *
 * Gareth, 20 October 2026: on the Board (each tab) and the Engineers view,
 * everyone can drag a column by its header to where they want it. The order
 * is theirs alone and is remembered on any device (users.ui_preferences
 * .column_order, saved by PUT /auth/me/column-order). Unassigned always stays
 * first and cannot be moved. A column the order does not know yet (a new
 * engineer, a stage switched on in Settings) goes on the end.
 *
 * A column drag carries its own data type, so the Engineers view can tell it
 * apart from a card being dragged onto an engineer.
 * =============================================================================
 */

import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';

export const COLUMN_DRAG_TYPE = 'application/x-ids-column';

/** The columns in the saved order; ones the order does not name go on the end, in their usual order. */
export function applyColumnOrder(columns, order, keyOf) {
  const list = Array.isArray(columns) ? columns : [];
  if (!Array.isArray(order) || order.length === 0) return list;
  const rank = new Map(order.map((key, index) => [key, index]));
  return list
    .map((column, index) => ({ column, index, rank: rank.has(keyOf(column)) ? rank.get(keyOf(column)) : Infinity }))
    .sort((a, b) => (a.rank - b.rank) || (a.index - b.index))
    .map(entry => entry.column);
}

/**
 * The keys after dragging one column onto another: it takes the place of the
 * column it was dropped on (so dropped rightwards it lands after it, leftwards
 * before it).
 */
export function moveColumnKey(keys, fromKey, toKey) {
  const from = keys.indexOf(fromKey);
  const to = keys.indexOf(toKey);
  if (from < 0 || to < 0 || from === to) return keys;
  const next = keys.filter(key => key !== fromKey);
  next.splice(to, 0, fromKey);
  return next;
}

export const isColumnDrag = (event) => Array.from(event?.dataTransfer?.types || []).includes(COLUMN_DRAG_TYPE);

export function useColumnOrder(view) {
  const { user, authFetch, updateUser } = useAuth();
  const saved = user?.ui_preferences?.column_order?.[view];
  const savedKey = JSON.stringify(Array.isArray(saved) ? saved : []);
  const [order, setOrder] = useState(() => JSON.parse(savedKey));
  const [dragging, setDragging] = useState(null);
  const [over, setOver] = useState(null);
  const [error, setError] = useState('');

  // Another tab or device may have saved a newer order.
  useEffect(() => { setOrder(JSON.parse(savedKey)); }, [view, savedKey]);

  const save = async (next) => {
    setOrder(next);
    setError('');
    try {
      const response = await authFetch('/api/v1/auth/me/column-order', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ view, order: next })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'The column order could not be saved.');
      if (data.ui_preferences && typeof updateUser === 'function') updateUser({ ui_preferences: data.ui_preferences });
    } catch (err) {
      // The new order still shows; it is just not remembered.
      setError(err.message === 'Failed to fetch' ? 'The column order could not be saved. Check your signal.' : err.message);
    }
  };

  return useMemo(() => ({
    arrange: (columns, keyOf) => applyColumnOrder(columns, order, keyOf),
    customised: order.length > 0,
    reset: () => save([]),
    error,
    dragging,
    over,
    /** Props for a column header that can be dragged. */
    headerProps: (key) => ({
      draggable: true,
      title: 'Drag to move this column',
      onDragStart: (event) => {
        event.dataTransfer.setData(COLUMN_DRAG_TYPE, key);
        event.dataTransfer.effectAllowed = 'move';
        setDragging(key);
      },
      onDragEnd: () => { setDragging(null); setOver(null); }
    }),
    /**
     * Handlers for a column a header can be dropped on. Each returns true when
     * it dealt with a column drag, so the caller can leave card drags alone.
     */
    dragOver: (event, key) => {
      if (!isColumnDrag(event)) return false;
      event.preventDefault();
      if (over !== key) setOver(key);
      return true;
    },
    drop: (event, key, keys) => {
      if (!isColumnDrag(event)) return false;
      event.preventDefault();
      const fromKey = event.dataTransfer.getData(COLUMN_DRAG_TYPE);
      setDragging(null);
      setOver(null);
      const next = moveColumnKey(keys, fromKey, key);
      if (next !== keys) save(next);
      return true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [order, error, dragging, over, view]);
}
